import { useCallback, useState } from 'react';
import { getEpisodeService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableQueryParams, TableDataResponse } from '../types/table';
import type {
  Episode,
  EpisodeCreateRequest,
  EpisodeDetailResponse,
  EpisodeUpdateRequest,
  EpisodesQueryParams,
} from '../types/api/episode';
import type {
  CompleteMultipartUploadRequest,
  InitiateMultipartUploadRequest,
  InitiateMultipartUploadResponse,
  VideoMultipartPresignResponse,
  VideoMultipartRegenerateRequest,
  VideoMultipartStatusResponse,
  VideoVariant,
} from '../types/api/movie';

/**
 * Hook for episode listing scoped to a specific drama/series (dramaId).
 *
 * Designed to integrate with the DataTable component.
 */
export function useEpisodes(dramaId: string) {
  const episodeService = getEpisodeService();
  const [seasonFilter, setSeasonFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const fetchEpisodes: FetchDataFn<Episode> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<Episode>> => {
      try {
        const apiParams: EpisodesQueryParams = {
          page: params.page, // backend now 1-based
          size: params.pageSize,
        };

        if (params.search) {
          apiParams.keyword = params.search;
        }
        if (seasonFilter != null) {
          apiParams.season = seasonFilter;
        }
        if (statusFilter) {
          apiParams.status = statusFilter;
        }

        const response = await episodeService.getEpisodes(dramaId, apiParams);

        return {
          data: response.items,
          pagination: {
            page: response.page,
            pageSize: response.size,
            total: response.totalItems,
            totalPages: response.totalPages,
            hasNext: response.hasNext,
            hasPrev: response.hasPrevious,
          },
        };
      } catch (error) {
        console.error('Failed to fetch episodes:', error);
        throw new Error('Failed to load episodes. Please try again.');
      }
    },
    [episodeService, dramaId, seasonFilter, statusFilter]
  );

  return {
    fetchEpisodes,
    seasonFilter,
    setSeasonFilter,
    statusFilter,
    setStatusFilter,
  };
}

/**
 * Hook for episode actions (CRUD) scoped to a specific drama/series (dramaId).
 */
export function useEpisodeActions(dramaId: string) {
  const episodeService = getEpisodeService();

  const createEpisodeWithJson = useCallback(
    async (payload: EpisodeCreateRequest): Promise<Episode> => {
      try {
        return await episodeService.createEpisodeWithJson(dramaId, payload);
      } catch (error) {
        console.error('Failed to create episode (JSON):', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const createEpisodeWithThumbnail = useCallback(
    async (payload: EpisodeCreateRequest, thumbnailFile?: File): Promise<Episode> => {
      try {
        console.log('dramaId in hook', dramaId);
        return await episodeService.createEpisodeWithThumbnail(dramaId, payload, thumbnailFile);
      } catch (error) {
        console.error('Failed to create episode with thumbnail:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const updateEpisode = useCallback(
    async (episodeId: string, payload: EpisodeUpdateRequest): Promise<Episode> => {
      try {
        return await episodeService.updateEpisode(dramaId, episodeId, payload);
      } catch (error) {
        console.error('Failed to update episode:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const softDeleteEpisode = useCallback(
    async (episodeId: string): Promise<void> => {
      try {
        await episodeService.softDeleteEpisode(dramaId, episodeId);
      } catch (error) {
        console.error('Failed to delete episode:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const permanentDeleteEpisode = useCallback(
    async (episodeId: string): Promise<void> => {
      try {
        await episodeService.permanentDeleteEpisode(dramaId, episodeId);
      } catch (error) {
        console.error('Failed to permanently delete episode:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const getEpisode = useCallback(
    async (episodeId: string): Promise<EpisodeDetailResponse> => {
      try {
        return await episodeService.getEpisode(dramaId, episodeId);
      } catch (error) {
        console.error('Failed to fetch episode:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const getSeasons = useCallback(async (): Promise<number[]> => {
    try {
      return await episodeService.getSeasons(dramaId);
    } catch (error) {
      console.error('Failed to fetch seasons:', error);
      throw error;
    }
  }, [episodeService, dramaId]);

  const initiateVideoUpload = useCallback(
    async (
      episodeId: string,
      payload: InitiateMultipartUploadRequest
    ): Promise<InitiateMultipartUploadResponse> => {
      try {
        return await episodeService.initiateMultipartVideoUpload(dramaId, episodeId, payload);
      } catch (error) {
        console.error('Failed to initiate episode video upload:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const completeVideoUpload = useCallback(
    async (
      episodeId: string,
      payload: CompleteMultipartUploadRequest
    ): Promise<VideoVariant> => {
      try {
        return await episodeService.completeMultipartVideoUpload(dramaId, episodeId, payload);
      } catch (error) {
        console.error('Failed to complete episode video upload:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const getMultipartUploadStatus = useCallback(
    async (
      episodeId: string,
      key: string,
      uploadId: string
    ): Promise<VideoMultipartStatusResponse> => {
      try {
        return await episodeService.getMultipartUploadStatus(dramaId, episodeId, key, uploadId);
      } catch (error) {
        console.error('Failed to get episode multipart upload status:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const regenerateMultipartPartUrls = useCallback(
    async (
      episodeId: string,
      request: VideoMultipartRegenerateRequest
    ): Promise<VideoMultipartPresignResponse> => {
      try {
        return await episodeService.regenerateMultipartPartUrls(dramaId, episodeId, request);
      } catch (error) {
        console.error('Failed to regenerate episode multipart part URLs:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const abortVideoUpload = useCallback(
    async (episodeId: string, params: { key: string; uploadId: string }): Promise<void> => {
      try {
        await episodeService.abortMultipartVideoUpload(dramaId, episodeId, params);
      } catch (error) {
        console.error('Failed to abort episode video upload:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const uploadVideoDirect = useCallback(
    async (episodeId: string, formData: FormData): Promise<VideoVariant> => {
      try {
        return await episodeService.uploadEpisodeVideoDirect(dramaId, episodeId, formData);
      } catch (error) {
        console.error('Failed to upload episode video directly:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  const deleteVideo = useCallback(
    async (episodeId: string, fileId: string): Promise<void> => {
      try {
        await episodeService.deleteEpisodeVideo(dramaId, episodeId, fileId);
      } catch (error) {
        console.error('Failed to delete episode video:', error);
        throw error;
      }
    },
    [episodeService, dramaId]
  );

  return {
    createEpisodeWithJson,
    createEpisodeWithThumbnail,
    updateEpisode,
    softDeleteEpisode,
    permanentDeleteEpisode,
    getEpisode,
    getSeasons,
    initiateVideoUpload,
    completeVideoUpload,
    getMultipartUploadStatus,
    regenerateMultipartPartUrls,
    abortVideoUpload,
    uploadVideoDirect,
    deleteVideo,
  };
}